import React, { useCallback } from 'react';
import { CheckboxWithLabel } from 'formik-material-ui';
import { ErrorMessage, Formik, Field } from 'formik';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import useAuthToken from 'utils/useAuthToken';
import { loginLoadingSelector } from 'reducers/app.reducer';
import { createNewPassword } from 'actions/app.actions';
import { InputFieldWithLabel, CGULabel, PrimaryButton } from 'components';
import { getPasswordInputSchema } from 'schema';
import { ROUTES } from '../../constants';
import * as S from './styles';
import { BasicTextWithUIGuidelines } from 'component-library/atoms/BasicTextWithUIGuidelines';
import { LinkWithUIGuidelines } from 'component-library/atoms/LinkWithUIGuidelines';
import { ICustomTheme } from 'interfaces';
import { useTheme } from '@material-ui/core';

type formValuesType = {
  password: string;
  confirmPassword: string;
  isCGUAccepted: boolean;
};

const initialValues: formValuesType = {
  password: '',
  confirmPassword: '',
  isCGUAccepted: false,
};

interface ICreateNewPasswordForm {
  token: string;
}

export default function CreateNewPasswordForm({
  token,
}: ICreateNewPasswordForm) {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const { setCookie } = useAuthToken();
  const isLoading = useSelector(loginLoadingSelector);

  const passwordValidator = getPasswordInputSchema(t);

  const formSchema = Yup.object().shape({
    password: passwordValidator,
    confirmPassword: passwordValidator.oneOf(
      [Yup.ref('password')],
      t('core.password.mismatch'),
    ),
    isCGUAccepted: Yup.boolean().oneOf(
      [true],
      t('auth.signup.required_terms_and_conditions'),
    ),
  });

  const handleSubmit = useCallback(
    ({ password, isCGUAccepted }: formValuesType) => {
      dispatch(
        createNewPassword.request(
          { token, password, isCGUAccepted },
          (token) => {
            setCookie(token);
            history.push(ROUTES.HOME);
          },
        ),
      );
    },
    [dispatch, token, history, setCookie],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formSchema}
      onSubmit={handleSubmit}
    >
      <S.Form>
        <InputFieldWithLabel
          placeholder={t('auth.global.password_placeholder')}
          label={t('auth.createNewPassword.createMyPassword')}
          name="password"
          textCase="sentence"
          type="password"
        />
        <InputFieldWithLabel
          placeholder={t('auth.global.confirm_password_placeholder')}
          label={t('auth.createNewPassword.confirmMyPassword')}
          name="confirmPassword"
          textCase="sentence"
          type="password"
        />
        <S.CheckboxFormControl
          control={
            <Field
              component={CheckboxWithLabel}
              type="checkbox"
              name="isCGUAccepted"
            />
          }
          label={<CGULabel />}
        />
        <ErrorMessage
          name="isCGUAccepted"
          component={(props) => <S.Error {...props} variant="caption" />}
        />
        <S.SubmitButtonContainer>
          <PrimaryButton
            label={t('auth.reset_password.submit')}
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </S.SubmitButtonContainer>
        <S.ConnectMessageContainer>
          <BasicTextWithUIGuidelines
            text={t('auth.global.iAlreadyHaveAnAccount')}
          />{' '}
          <LinkWithUIGuidelines
            color={theme.colors.paletteFromBO.main}
            fontWeight="bold"
            url={ROUTES.LOGIN}
            textLink={t('auth.global.login')}
            isInternalLink={true}
            withUnderline
          />
        </S.ConnectMessageContainer>
      </S.Form>
    </Formik>
  );
}
